/**
 * Configuration → Business Opportunities → Dimensions — admins only. What
 * every idea is scored on: a label, the question research asks the model
 * (the description), a weight, and the Impact × Feasibility axis it feeds.
 *
 * A read-only table; Add dimension and each row's Edit open a dialog. The
 * dialog changes a draft, not the server: the weights must add up to 100%
 * across rows, so no single row can be saved on its own. The Save bar under
 * the table (Reset drops the draft) commits the whole draft behind one confirmation; the table's
 * footer row shows the running total. Retired rows leave the set (past research keeps their
 * scores); new rows get a key from their label. A save that changes what
 * research asks (not only axes) makes a new prompt version, which research
 * uses from its next run.
 */


import { Plus } from 'lucide-react';

import { ConfirmDialog } from '@/ui/ConfirmDialog';
import { FormError, LoadFailure, LoadingCard } from '@/ui/StateCard';
import { SectionActions } from '@/ui/SectionActions';
import { buttonState } from '@/ui/ActionButton';
import { SaveBar } from '@/ui/SaveBar';
import { DimensionDialog } from './DimensionDialog';
import { DimensionsTable } from './DimensionsTable';
import { useDimensionsSection, type DimensionsState } from './useDimensionsSection';

function RetiredNote({ count }: { count: number }) {
  const one = count === 1;
  return (
    <p className="hint">
      {count} retired {one ? 'dimension is' : 'dimensions are'} kept so past research still shows
      {one ? ' its score' : ' their scores'}.
    </p>
  );
}

function SaveConfirm({ state }: { state: DimensionsState }) {
  const { change, save, setConfirming } = state;
  return (
    <ConfirmDialog
      title="Save the dimensions?"
      confirmLabel={change.retiring ? 'Retire and save' : 'Save'}
      danger={change.retiring}
      pending={save.isPending}
      error={save.error}
      onConfirm={state.submit}
      onClose={() => setConfirming(false)}
    >
      <p>{change.parts.join(' · ')}.</p>
      <p className="hint">
        {change.axesOnly
          ? 'Only the opportunity matrix moves: totals, rankings and research stay as they are.'
          : 'Totals and the ranking update at once. This makes a new prompt version: research uses it from its next run, and verdicts change only when research runs again.'}
        {change.retiring && ' A retired dimension cannot be brought back, though past research keeps its score.'}
      </p>
    </ConfirmDialog>
  );
}

function EditDialog({ state, editing }: { state: DimensionsState; editing: NonNullable<DimensionsState['editing']> }) {
  const initial = editing === 'new' ? null : editing;
  return (
    <DimensionDialog
      initial={initial}
      takenLabels={state.takenBy(initial ? initial.id : null)}
      onApply={state.apply}
      onClose={() => state.setEditing(null)}
    />
  );
}

function DimensionsCard({ state }: { state: DimensionsState }) {
  const { changed, problems, save, editable } = state;
  return (
    <div className="card">
      <div className="card-body stack-md">
        <DimensionsTable state={state} />

        {state.retiredCount > 0 && <RetiredNote count={state.retiredCount} />}
        {changed && problems.length > 0 && (
          <div className="alert alert-warning form-alert" role="status">
            <div className="a-body">{problems.join(' ')}</div>
          </div>
        )}
        <FormError error={!state.confirming ? save.error : undefined} />

        {editable && (
          <SaveBar
            changed={changed}
            valid={state.canSave}
            state={buttonState(save)}
            onReset={state.reset}
            onSave={() => state.setConfirming(true)}
          />
        )}
      </div>
    </div>
  );
}

export function DimensionsSection() {
  const state = useDimensionsSection();
  const { dimensions, editing } = state;

  if (dimensions.isPending) return <LoadingCard label="Loading the dimensions" />;
  if (dimensions.error) return <LoadFailure error={dimensions.error} onRetry={() => void dimensions.refetch()} />;

  return (
    <>
      {state.editable && (
        <SectionActions>
          <button type="button" className="btn btn-secondary" disabled={state.addDisabled} onClick={() => state.setEditing('new')}>
            <Plus aria-hidden="true" /> Add dimension
          </button>
        </SectionActions>
      )}

      <DimensionsCard state={state} />

      {editing && <EditDialog state={state} editing={editing} />}

      {state.confirming && <SaveConfirm state={state} />}
    </>
  );
}
